// ===================================================================
// SHELTER — Map View
// Leaflet map showing shelter locations with status color-coding
// ===================================================================

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Group, Paper, Badge, Stack, Text, Button, Loader, Center,
} from '@mantine/core';
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useShelterStore } from '../../store/shelterStore';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import { useAuthStore } from '../../store/authStore';
import { SHELTER_STATUS_COLORS, SHELTER_TYPE_LABELS } from '../../types/shelter';
import type { ShelterStatus } from '../../types/shelter';

// Fix leaflet default icon
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = L.icon({ iconUrl, shadowUrl: iconShadow, iconAnchor: [12, 41], popupAnchor: [1, -34] });
L.Marker.prototype.options.icon = DefaultIcon;

// Custom colored circle icons
function makeIcon(color: string) {
  return L.divIcon({
    className: '',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
  });
}

const STATUS_ICONS: Record<ShelterStatus, L.DivIcon> = {
  open: makeIcon(SHELTER_STATUS_COLORS.open),
  closed: makeIcon(SHELTER_STATUS_COLORS.closed),
  at_capacity: makeIcon(SHELTER_STATUS_COLORS.at_capacity),
  standby: makeIcon(SHELTER_STATUS_COLORS.standby),
};

function ShelterZoneLayer() {
  const { activeZones } = useIncidentZones();
  return <ZoneMapOverlay zones={activeZones} interactive />;
}

export default function ShelterMap() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { shelters, loadingShelters, loadShelters } = useShelterStore();

  useEffect(() => { loadShelters(); }, [loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iShelters = incidentId ? shelters : [];

  const mappableShelters = useMemo(() =>
    iShelters.filter(s => {
      const lat = parseFloat(s.latitude);
      const lng = parseFloat(s.longitude);
      return lat && lng && !isNaN(lat) && !isNaN(lng);
    }),
  [iShelters]);

  const parseFacilities = (json: string): string[] => {
    try { return JSON.parse(json); } catch { return []; }
  };

  const defaultCenter: [number, number] = [33.749, -84.388];

  const bounds = useMemo(() => {
    if (mappableShelters.length === 0) return null;
    const points = mappableShelters.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)] as [number, number]);
    return L.latLngBounds(points);
  }, [mappableShelters]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3} c="black">Shelter Map</Title>
        <Group gap="lg">
          {Object.entries(SHELTER_STATUS_COLORS).map(([status, color]) => (
            <Group key={status} gap={4}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 0 2px rgba(0,0,0,.3)' }} />
              <Text size="xs" tt="capitalize">{status.replace(/_/g, ' ')}</Text>
            </Group>
          ))}
          <Text size="xs" c="dimmed">{mappableShelters.length} shelters mapped</Text>
          {loadingShelters && <Loader size="xs" />}
        </Group>
      </Group>

      <Paper withBorder style={{ height: 'calc(100vh - 220px)', minHeight: 400, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
        {loadingShelters && mappableShelters.length === 0 && (
          <Center style={{ position: 'absolute', inset: 0, zIndex: 1000, background: 'rgba(255,255,255,0.8)' }}>
            <Stack align="center" gap="xs">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">Loading shelter data...</Text>
            </Stack>
          </Center>
        )}
        <MapContainer
          center={bounds ? undefined : defaultCenter}
          zoom={bounds ? undefined : 10}
          bounds={bounds ?? undefined}
          boundsOptions={{ padding: [40, 40] }}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer attribution='&copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topo">
              <TileLayer attribution='&copy; OpenTopoMap' url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Dark">
              <TileLayer attribution='&copy; CartoDB' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
            </LayersControl.BaseLayer>
          </LayersControl>

          <ShelterZoneLayer />

          {mappableShelters.map(s => {
            const lat = parseFloat(s.latitude);
            const lng = parseFloat(s.longitude);
            const facilities = parseFacilities(s.facilities);
            const fillPct = s.capacity > 0 ? Math.round((s.currentPopulation / s.capacity) * 100) : 0;

            return (
              <Marker key={s.id} position={[lat, lng]} icon={STATUS_ICONS[s.status] ?? STATUS_ICONS.standby}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 220 }}>
                    <Text fw={700} size="sm">{s.name}</Text>
                    <Group gap={6}>
                      <Badge size="xs" color={SHELTER_STATUS_COLORS[s.status]} variant="filled">
                        {s.status.replace(/_/g, ' ')}
                      </Badge>
                      {s.shelterType && (
                        <Badge size="xs" variant="light">
                          {SHELTER_TYPE_LABELS[s.shelterType] ?? s.shelterType}
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs">
                      Population: <b>{s.currentPopulation}</b> / {s.capacity} ({fillPct}%)
                    </Text>
                    {s.address && <Text size="xs" c="dimmed">{s.address}</Text>}
                    {facilities.length > 0 && (
                      <Text size="xs" c="dimmed">{facilities.length} facilities</Text>
                    )}
                    {s.operatingOrg && <Text size="xs" c="dimmed">Org: {s.operatingOrg}</Text>}
                    <Button size="compact-xs" variant="light" onClick={() => navigate('/shelter/shelters')}>
                      View Shelter Details
                    </Button>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </Paper>
    </Stack>
  );
}
